import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useSyncExternalStore } from 'react'

import { type RevisionConflict, revisionConflictMessage } from '@/services/revisions/revisionWrite'

type Listener = () => void

let current: RevisionConflict | null = null
const listeners = new Set<Listener>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return current
}

/**
 * Opens the app-wide conflict dialog. Editors call it when a save comes back 409 `revision_conflict`,
 * whether the user or the Workforce Assistant pressed Save.
 */
export function showRevisionConflict(conflict: RevisionConflict): void {
  current = conflict
  emit()
}

export function closeRevisionConflict(): void {
  current = null
  emit()
}

export interface RevisionConflictDialogViewProps {
  conflict: RevisionConflict | null
  onClose: () => void
  onReload: () => void
}

export function RevisionConflictDialogView({ conflict, onClose, onReload }: RevisionConflictDialogViewProps) {
  return (
    <Dialog open={!!conflict} onClose={onClose} aria-labelledby="revision-conflict-title" PaperProps={{ id: 'revision-conflict-dialog' }}>
      <DialogTitle id="revision-conflict-title">Someone else saved this first</DialogTitle>
      <DialogContent>
        <DialogContentText id="revision-conflict-message">{conflict ? revisionConflictMessage(conflict) : ''}</DialogContentText>
        <DialogContentText sx={{ mt: 1 }} variant="body2">
          Your changes were not saved. Reloading discards them.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button id="revision-conflict-close-button" onClick={onClose}>
          Keep editing
        </Button>
        <Button id="revision-conflict-reload-button" variant="contained" onClick={onReload}>
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Mounted once at the app root. Shows the latest conflict passed to {@link showRevisionConflict}. */
export default function RevisionConflictDialog() {
  const conflict = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return (
    <RevisionConflictDialogView
      conflict={conflict}
      onClose={closeRevisionConflict}
      onReload={() => {
        closeRevisionConflict()
        window.location.reload()
      }}
    />
  )
}
